/**
 * Travel Desk — the money of a trip (ADR 0030, plan §5.8, Phase 4: TD-4.2–4.19). Mounted under
 * `/travel/money`. Declared on its own (invariant 8) before the routes and screens consume it.
 * Shapes: `travel/money-schemas.ts`; pure logic: `travel/money.ts`, `claim-link.ts`,
 * `allowance-draft.ts`, `settlement-rounds.ts`, `recovery.ts`.
 *
 * THE LOOP.
 *   1. ADVANCE (TD-4.2, 4.14, 4.15). On an approved trip the traveller asks for an advance
 *      (`getTripAdvanceDraft` → `requestTripAdvance`), accepting the recovery consent (D22). It is a
 *      normal `ExpenseAdvance` linked to the trip; within the policy limit it is approved at once,
 *      otherwise it goes through the `expense_advance` workflow. Refused while another advance is
 *      overdue when the policy says so (EC-37). Finance pays it out with the existing
 *      `expense.disburseAdvance`.
 *   2. CLAIMS (TD-4.3, 4.4, 4.17). Trip-linked claims are normal `ExpenseClaim`s carrying
 *      `travelRequestId`, inside the trip's dates ± 2 days (D32, pre-trip fares excepted).
 *      `listTripClaims` shows them with the company-paid bookings and a duplicate flag;
 *      `getAllowanceDraft` / `createAllowanceClaims` file per diem, lodging in lieu and mileage as
 *      draft claims priced by the policy (`buildAllowanceDraft` → `computePerDiem`).
 *   3. SETTLEMENT ROUNDS (TD-4.5, 4.13). The traveller submits claims in a round
 *      (`submitSettlement`: `pre_trip` for self-booked fares, `main` from the trip's last day,
 *      `supplementary` for late receipts within the window). The single-claim submit REFUSES a
 *      trip-linked claim (409 `claim_needs_settlement`, D9): a trip's claims are decided only here.
 *      The `trip_settlement` workflow decides it; the approver approves or rejects each expense
 *      (D8, `approveSettlement.lines`), or rejects the round, or asks a question.
 *   4. NET-OFF (TD-4.5, 4.15). Finance nets an approved round against the trip's advance in ONE
 *      transaction (`netOffSettlement`, `planSettlementNetting`): the trip's own advance first, an
 *      unpaid trip advance cancelled (EC-36), optionally the traveller's other overdue advances
 *      (EC-37). What the advance does not cover is paid through the normal claim payout.
 *   5. CLOSE (TD-4.18). `noMoreExpenses`, or the window ending, closes the trip once nothing is
 *      outstanding (`decideTripClose`); an advance left outstanding goes to RECOVERY (D4 ladder):
 *      repayment, payroll deduction (D22, `recoverViaPayroll`), F&F (D23), write-off with the org
 *      owner's approval (TD-4.8).
 *
 * WHO MAY CALL WHAT. The traveller works their own trip (`travel:request`, `expense:submit`,
 * `expense.advance:request`); a trip-scoped read declared `null` is open to the traveller, the
 * people deciding the trip or round, `travel:read` and finance (`expense:settle` /
 * `expense.advance:manage`) and is a 404 for anyone else. Deciding a round or a write-off is
 * workflow-assignment gated (`null`), like `expense.approveClaim`. Net-off is `expense:settle`;
 * recovery and write-off proposals are `expense.advance:manage`.
 *
 * ERROR CODES. A 409 / 422 carries `details.code` (one of `TRAVEL_ERROR_CODES`, the money ones in
 * `TRAVEL_MONEY_ERROR_CODES`); each route lists the codes it sends. Every edit sends `version`
 * (409 `stale_version`) and every money write takes an `Idempotency-Key` (invariant 10): a retry
 * returns the original result (EC-59).
 *
 * MONEY. Decimal strings with the currency beside them; never summed across currencies.
 *
 * MODULE SWITCH. With `TravelSettings.enabled` off (D33) every route answers 404 `module_disabled`.
 * A trial organisation's read-only mode denies every write (EC-48).
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  errorBodySchema,
  idempotencyKeySchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import { noMoreExpensesBodySchema } from '../travel/schemas.js';
import {
  advanceRecoveryViewSchema,
  advanceWriteOffSchema,
  approveTravelSettlementBodySchema,
  checkClaimDuplicateQuerySchema,
  claimDuplicateCheckSchema,
  createAllowanceClaimsBodySchema,
  createdAllowanceClaimsSchema,
  decideAdvanceWriteOffBodySchema,
  linkableTripSchema,
  listAdvanceWriteOffsQuerySchema,
  listLinkableTripsQuerySchema,
  listOutstandingAdvancesQuerySchema,
  listTravelSettlementsQuerySchema,
  netOffTravelSettlementBodySchema,
  outstandingAdvanceRowSchema,
  proposeAdvanceWriteOffBodySchema,
  recordAdvanceRepaymentBodySchema,
  recoverAdvanceViaPayrollBodySchema,
  rejectTravelSettlementBodySchema,
  requestTripAdvanceBodySchema,
  reverseAdvanceRecoveryBodySchema,
  submitTravelSettlementBodySchema,
  travelSettlementAnswerBodySchema,
  travelSettlementDraftQuerySchema,
  travelSettlementDraftSchema,
  travelSettlementQuestionBodySchema,
  travelSettlementSchema,
  travelSettlementSummarySchema,
  tripAdvanceDraftSchema,
  tripAdvanceResultSchema,
  tripAllowanceDraftSchema,
  tripClaimsSchema,
  tripMoneySummarySchema,
  withdrawAdvanceWriteOffBodySchema,
  withdrawTravelSettlementBodySchema,
} from '../travel/money-schemas.js';
import { routeMeta } from './meta.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({
  'idempotency-key': idempotencyKeySchema,
});

const tripParamsSchema = z.object({ travelRequestId: uuidSchema });
const settlementParamsSchema = z.object({ settlementId: uuidSchema });
const advanceParamsSchema = z.object({ advanceId: uuidSchema });
const recoveryParamsSchema = z.object({ recoveryId: uuidSchema });
const writeOffParamsSchema = z.object({ writeOffId: uuidSchema });

export const travelSettlementContract = c.router(
  {
    // ---- Claims and allowances (handlers: `travelClaimLinkHandlers`, lib/travel/claims) ---------

    /**
     * The trips a claim may carry (the claim form's picker): the caller's own (or, with
     * `expense.claim:file_for` over them, `personId`'s) trips in a status that takes expenses,
     * narrowed to those whose window holds `expenseDate`. Newest first, at most 50.
     */
    listLinkableTrips: {
      method: 'GET',
      path: '/linkable-trips',
      query: listLinkableTripsQuerySchema,
      responses: {
        200: z.array(linkableTripSchema),
        401: errorBodySchema,
        /** `personId` is someone the caller may not file for. */
        403: errorBodySchema,
        /** `module_disabled`. */
        404: errorBodySchema,
      },
      summary: 'The trips an expense claim may be linked to',
      metadata: routeMeta(null),
    },

    listTripClaims: {
      method: 'GET',
      path: '/trips/:travelRequestId/claims',
      pathParams: tripParamsSchema,
      responses: {
        200: tripClaimsSchema,
        401: errorBodySchema,
        /** `module_disabled`, or not a trip the caller may read. */
        404: errorBodySchema,
      },
      summary: "A trip's linked claims, the bookings the company paid, and possible duplicates",
      metadata: routeMeta(null),
    },

    checkClaimDuplicate: {
      method: 'GET',
      path: '/trips/:travelRequestId/claims/duplicate-check',
      pathParams: tripParamsSchema,
      query: checkClaimDuplicateQuerySchema,
      responses: {
        200: claimDuplicateCheckSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Before saving a claim: does it look like a booking the company already paid?',
      metadata: routeMeta(null),
    },

    getTripAdvanceDraft: {
      method: 'GET',
      path: '/trips/:travelRequestId/advance',
      pathParams: tripParamsSchema,
      responses: {
        200: tripAdvanceDraftSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** `module_disabled`, or not the caller's trip. */
        404: errorBodySchema,
      },
      summary: "What the 'ask for an advance' form opens with: limit, consent text, what blocks it",
      metadata: routeMeta('expense.advance:request'),
    },

    /**
     * Creates and submits an `ExpenseAdvance` linked to the trip, recording the consent. Within the
     * policy limit it is approved at once (`autoApproved: true`, TD-4.14); above it the normal
     * advance approval runs. Errors: 409 `trip_not_approved`, `trip_advance_exists`,
     * `advance_blocked_overdue`; 422 `recovery_consent_required`, `advance_currency_mismatch`,
     * `advance_over_policy` (only when the expense module's own ceilings refuse it: above the
     * travel limit is not an error, it just needs approval).
     */
    requestTripAdvance: {
      method: 'POST',
      path: '/trips/:travelRequestId/advance',
      pathParams: tripParamsSchema,
      headers: idempotencyHeadersSchema,
      body: requestTripAdvanceBodySchema,
      responses: {
        201: tripAdvanceResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Ask for an advance against an approved trip, accepting the recovery consent',
      metadata: routeMeta('expense.advance:request'),
    },

    getAllowanceDraft: {
      method: 'GET',
      path: '/trips/:travelRequestId/allowances',
      pathParams: tripParamsSchema,
      responses: {
        200: tripAllowanceDraftSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** `trip_not_ended` (drafts are for a trip that has ended or is on its last day). */
        409: errorBodySchema,
      },
      summary: 'Per diem, lodging in lieu and mileage priced by the policy for a finished trip',
      metadata: routeMeta('travel:request'),
    },

    /**
     * Files the chosen allowances as DRAFT claims on the trip, priced on the server. Errors: 409
     * `trip_not_ended`, `allowance_already_claimed`, `settlement_window_closed`; 422
     * `per_diem_not_configured`, `mileage_rate_missing`, `expense_category_missing`,
     * `nothing_to_claim`.
     */
    createAllowanceClaims: {
      method: 'POST',
      path: '/trips/:travelRequestId/allowances',
      pathParams: tripParamsSchema,
      headers: idempotencyHeadersSchema,
      body: createAllowanceClaimsBodySchema,
      responses: {
        201: createdAllowanceClaimsSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'File the per diem, lodging in lieu and mileage drafts as claims on the trip',
      metadata: routeMeta('expense:submit'),
    },

    // ---- The trip's money and its settlement rounds (handlers: routes/travel-settlement.ts) -----

    getTripMoney: {
      method: 'GET',
      path: '/trips/:travelRequestId',
      pathParams: tripParamsSchema,
      responses: {
        200: tripMoneySummarySchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "A trip's money panel: advance, claimed, approved and the net per currency",
      metadata: routeMeta(null),
    },

    getSettlementDraft: {
      method: 'GET',
      path: '/trips/:travelRequestId/settlement-draft',
      pathParams: tripParamsSchema,
      query: travelSettlementDraftQuerySchema,
      responses: {
        200: travelSettlementDraftSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'What a settlement round of a trip would hold, and what net-off would do',
      metadata: routeMeta('travel:request'),
    },

    listTripSettlements: {
      method: 'GET',
      path: '/trips/:travelRequestId/settlements',
      pathParams: tripParamsSchema,
      responses: {
        200: z.array(travelSettlementSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "A trip's settlement rounds, oldest first, with their lines",
      metadata: routeMeta(null),
    },

    /**
     * Submits a round and starts its `trip_settlement` approval. Draft claims in it are submitted
     * with it. Errors: 409 `stale_version`, `trip_not_ended`, `main_settlement_exists`,
     * `main_settlement_required`, `settlement_in_progress`, `settlement_window_closed`; 422
     * `pre_trip_not_allowed`, `claim_not_settleable` (`expenseClaimIds`), `claim_not_linkable`.
     */
    submitSettlement: {
      method: 'POST',
      path: '/trips/:travelRequestId/settlements',
      pathParams: tripParamsSchema,
      headers: idempotencyHeadersSchema,
      body: submitTravelSettlementBodySchema,
      responses: {
        201: travelSettlementSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: "Submit a trip's expenses for settlement (pre-trip, main or supplementary round)",
      metadata: routeMeta('travel:request'),
    },

    /**
     * "No more expenses" (D13): the trip takes no further round. It closes now when no round is
     * waiting and nothing is outstanding; otherwise once that is true. Errors: 409
     * `stale_version`, `settlement_window_closed` (already said, or closed).
     */
    noMoreExpenses: {
      method: 'POST',
      path: '/trips/:travelRequestId/no-more-expenses',
      pathParams: tripParamsSchema,
      headers: idempotencyHeadersSchema,
      body: noMoreExpensesBodySchema,
      responses: {
        200: tripMoneySummarySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Mark a trip as having no more expenses to come',
      metadata: routeMeta('travel:request'),
    },

    /** Finance's review list: rounds by status (default `approved`, waiting for net-off). */
    listSettlements: {
      method: 'GET',
      path: '/settlements',
      query: listTravelSettlementsQuerySchema,
      responses: {
        200: paginatedSchema(travelSettlementSummarySchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Settlement rounds for finance to review and net off',
      metadata: routeMeta('expense:settle'),
    },

    /** Rounds with a workflow step waiting on the caller. A STATIC segment, declared before `getSettlement`. */
    listSettlementApprovals: {
      method: 'GET',
      path: '/settlements/approvals',
      query: paginationQuerySchema,
      responses: {
        200: paginatedSchema(travelSettlementSummarySchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "Trip settlement rounds waiting for the caller's decision",
      metadata: routeMeta(null),
    },

    getSettlement: {
      method: 'GET',
      path: '/settlements/:settlementId',
      pathParams: settlementParamsSchema,
      responses: {
        200: travelSettlementSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'One settlement round with its expenses, decisions and net-off',
      metadata: routeMeta(null),
    },

    /** The traveller pulls a pending round; its claims go back to draft. 409 `stale_version`, `settlement_not_pending`. */
    withdrawSettlement: {
      method: 'POST',
      path: '/settlements/:settlementId/withdraw',
      pathParams: settlementParamsSchema,
      headers: idempotencyHeadersSchema,
      body: withdrawTravelSettlementBodySchema,
      responses: {
        200: travelSettlementSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Withdraw a settlement round that is still waiting for a decision',
      metadata: routeMeta('travel:request'),
    },

    /**
     * Approve the step awaiting the caller, with per-expense decisions (D8). Errors: 409
     * `stale_version`, `settlement_not_pending`; 412 no step awaits the caller; 422
     * `unknown_settlement_line`, `approved_amount_exceeds_claim` (`expenseClaimIds`).
     */
    approveSettlement: {
      method: 'POST',
      path: '/settlements/:settlementId/approve',
      pathParams: settlementParamsSchema,
      headers: idempotencyHeadersSchema,
      body: approveTravelSettlementBodySchema,
      responses: {
        /** `status` stays `pending` while another step follows. */
        200: travelSettlementSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Approve a settlement round, rejecting individual expenses if needed',
      // Assignment-gated like `approveClaim`: the assigned approver may hold no permission.
      metadata: routeMeta(null),
    },

    rejectSettlement: {
      method: 'POST',
      path: '/settlements/:settlementId/reject',
      pathParams: settlementParamsSchema,
      headers: idempotencyHeadersSchema,
      body: rejectTravelSettlementBodySchema,
      responses: {
        200: travelSettlementSchema,
        401: errorBodySchema,
        404: errorBodySchema,
        /** `stale_version`, `settlement_not_pending`. */
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Reject a whole settlement round; its expenses go back to the traveller',
      metadata: routeMeta(null),
    },

    askSettlementQuestion: {
      method: 'POST',
      path: '/settlements/:settlementId/ask-question',
      pathParams: settlementParamsSchema,
      headers: idempotencyHeadersSchema,
      body: travelSettlementQuestionBodySchema,
      responses: {
        200: travelSettlementSchema,
        401: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Approver asks the traveller a question on a round, pausing its SLA',
      metadata: routeMeta(null),
    },

    answerSettlementQuestion: {
      method: 'POST',
      path: '/settlements/:settlementId/answer-question',
      pathParams: settlementParamsSchema,
      headers: idempotencyHeadersSchema,
      body: travelSettlementAnswerBodySchema,
      responses: {
        200: travelSettlementSchema,
        401: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: "The traveller answers the approver's question, resuming the round's SLA",
      metadata: routeMeta(null),
    },

    /**
     * Finance's batch net-off of an approved round, in one transaction with advisory locks on each
     * advance and claim (`planSettlementNetting`). Writes `advanceId` / `advanceAppliedAmount` on
     * each claim, `claim_net_off` ledger rows, decrements the advances, settles a claim the advance
     * covers in full (`payoutMode: advance`), cancels an unpaid trip advance (EC-36) and marks the
     * round `settled`. Errors: 409 `stale_version`, `settlement_not_approved`.
     */
    netOffSettlement: {
      method: 'POST',
      path: '/settlements/:settlementId/net-off',
      pathParams: settlementParamsSchema,
      headers: idempotencyHeadersSchema,
      body: netOffTravelSettlementBodySchema,
      responses: {
        200: travelSettlementSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: "Net an approved settlement round off against the trip's advance",
      metadata: routeMeta('expense:settle'),
    },

    // ---- Recovery (D4, D22, TD-4.6, 4.19) and write-off (TD-4.8) ----------------------------------

    /** Finance's recovery queue: paid-out advances with money outstanding. */
    listOutstandingAdvances: {
      method: 'GET',
      path: '/advances/outstanding',
      query: listOutstandingAdvancesQuerySchema,
      responses: {
        200: paginatedSchema(outstandingAdvanceRowSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Advances with money still outstanding, for recovery',
      metadata: routeMeta('expense.advance:manage'),
    },

    /**
     * One advance's recovery: the ledger, payroll deductions scheduled, and (finance only) the
     * plan. The traveller reads their own with `plan: null`.
     */
    getAdvanceRecovery: {
      method: 'GET',
      path: '/advances/:advanceId/recovery',
      pathParams: advanceParamsSchema,
      responses: {
        200: advanceRecoveryViewSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "An advance's recovery ledger, scheduled payroll deductions and plan",
      // ai hidden: the plan's per-run cap is a fixed share of the traveller's gross pay, a salary
      // figure that must not reach a prompt.
      metadata: routeMeta(null, { ai: 'hidden' }),
    },

    /**
     * Schedule recovery through payroll (D22): an input on the next unlocked run of the traveller's
     * current legal entity, capped per run, the rest carried forward. The recovery row is written
     * when that run is finalized. Errors: 409 `advance_not_outstanding`,
     * `recovery_already_scheduled`, `no_open_payroll_run`; 422 `recovery_manual_only` (`reason`).
     */
    recoverViaPayroll: {
      method: 'POST',
      path: '/advances/:advanceId/recover-via-payroll',
      pathParams: advanceParamsSchema,
      headers: idempotencyHeadersSchema,
      body: recoverAdvanceViaPayrollBodySchema,
      responses: {
        200: advanceRecoveryViewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: "Recover an outstanding advance from the traveller's next payroll run",
      metadata: routeMeta('expense.advance:manage', { ai: 'hidden' }),
    },

    /** Money handed back. Errors: 409 `advance_not_outstanding`; 422 `amount_exceeds_outstanding`. */
    recordRepayment: {
      method: 'POST',
      path: '/advances/:advanceId/repayments',
      pathParams: advanceParamsSchema,
      headers: idempotencyHeadersSchema,
      body: recordAdvanceRepaymentBodySchema,
      responses: {
        200: advanceRecoveryViewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Record money the traveller handed back against an advance',
      metadata: routeMeta('expense.advance:manage', { ai: 'hidden' }),
    },

    /**
     * Undo a recovery row (EC-58): a finalized payroll deduction is refunded through an `arrears`
     * input on the next run plus a `reversal` row; a scheduled (unfinalized) one is simply removed
     * from its run. Errors: 409 `recovery_not_reversible`, `no_open_payroll_run`.
     */
    reverseRecovery: {
      method: 'POST',
      path: '/recoveries/:recoveryId/reverse',
      pathParams: recoveryParamsSchema,
      headers: idempotencyHeadersSchema,
      body: reverseAdvanceRecoveryBodySchema,
      responses: {
        200: advanceRecoveryViewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Reverse a recovery recorded by mistake',
      metadata: routeMeta('expense.advance:manage', { ai: 'hidden' }),
    },

    /**
     * Finance proposes writing off what is outstanding; the org owner decides through the
     * `advance_write_off` workflow. Errors: 409 `advance_not_outstanding`, `write_off_pending`;
     * 422 `amount_exceeds_outstanding`.
     */
    proposeWriteOff: {
      method: 'POST',
      path: '/advances/:advanceId/write-off',
      pathParams: advanceParamsSchema,
      headers: idempotencyHeadersSchema,
      body: proposeAdvanceWriteOffBodySchema,
      responses: {
        201: advanceWriteOffSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Propose writing off the outstanding part of an advance',
      metadata: routeMeta('expense.advance:manage'),
    },

    /** Proposals, newest first; `awaitingMyDecision` lists those whose step waits on the caller. */
    listWriteOffs: {
      method: 'GET',
      path: '/write-offs',
      query: listAdvanceWriteOffsQuerySchema,
      responses: {
        200: paginatedSchema(advanceWriteOffSchema),
        401: errorBodySchema,
        /** Without `awaitingMyDecision`, the full list needs `expense.advance:manage`. */
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Advance write-off proposals',
      metadata: routeMeta(null),
    },

    /**
     * On approval the `write_off` ledger row is written and the advance closes `written_off`.
     * Errors: 409 `stale_version`, `write_off_not_pending`; 412 no step awaits the caller.
     */
    decideWriteOff: {
      method: 'POST',
      path: '/write-offs/:writeOffId/decide',
      pathParams: writeOffParamsSchema,
      headers: idempotencyHeadersSchema,
      body: decideAdvanceWriteOffBodySchema,
      responses: {
        200: advanceWriteOffSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Approve or reject a proposed advance write-off',
      // Assignment-gated: the default `advance_write_off` definition asks the org owner.
      metadata: routeMeta(null),
    },

    withdrawWriteOff: {
      method: 'POST',
      path: '/write-offs/:writeOffId/withdraw',
      pathParams: writeOffParamsSchema,
      headers: idempotencyHeadersSchema,
      body: withdrawAdvanceWriteOffBodySchema,
      responses: {
        200: advanceWriteOffSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** `stale_version`, `write_off_not_pending`. */
        409: errorBodySchema,
      },
      summary: 'Withdraw a write-off proposal still waiting for a decision',
      metadata: routeMeta('expense.advance:manage'),
    },
  },
  { pathPrefix: '/travel/money' },
);

/** The route keys `travelClaimLinkHandlers` (apps/api/src/lib/travel/claims) implements. */
export const TRAVEL_CLAIM_LINK_ROUTE_KEYS = [
  'listLinkableTrips',
  'listTripClaims',
  'checkClaimDuplicate',
  'getTripAdvanceDraft',
  'requestTripAdvance',
  'getAllowanceDraft',
  'createAllowanceClaims',
] as const satisfies readonly (keyof typeof travelSettlementContract)[];
